import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { selectDefinitions } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-registry/assemble";
import type { SurfaceSelection } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";
import useIndustryTaxonomyQuery from "app/pages/sales-intelligence/modules/industry/queries/useIndustryTaxonomyQuery";
import { showsIndustryTaxonomy } from "app/pages/sales-intelligence/modules/industry/utils/industryVisibility";
import useGroupedTechnologiesQuery from "app/pages/sales-intelligence/modules/technology/queries/useGroupedTechnologiesQuery";
import { siCountryService } from "app/pages/sales-intelligence/services/siCountryService";
import { useActiveDemoId } from "app/pages/sales-intelligence/store/demo-mode/useDemoModeStore";
import type { SuppressionList } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import {
    selectIsListReady,
    selectSuppressionLists,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/suppressionListSelectors";
import useCurrentStaticList from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useCurrentStaticList";
import { selectOpportunityLists } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { useSignalsListViewModel } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalsListViewModel";
import { selectSignalsAreBeingFetched } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

import useLeadsDescriptionQuery from "../queries/useLeadsDescriptionQuery";
import { leadsFilterRegistry } from "../registry";
import type { CompanySelectObject } from "../types/description.dto";
import type { LeadsCapability } from "../types/leadsCapability";
import {
    type LeadsDataSource,
    type LeadsFilterBuildContext,
    leadsDataSourceMap,
} from "../types/leadsFilterBuildContext";
import type { LeadsFilterCategory } from "../types/leadsFilterCategory";

import { useLeadScoringFiltersData } from "./useLeadScoringFiltersData";

type UseLeadsFilterBuildContextParams = {
    readonly selection: SurfaceSelection<LeadsFilterCategory, LeadsCapability>;
    readonly applySuppressionListByDefault: boolean;
    readonly listCompanies?: readonly CompanySelectObject[];
};

type LeadsFilterBuildContextResult = {
    readonly ctx: LeadsFilterBuildContext;
    readonly ready: ReadonlySet<LeadsDataSource>;
    readonly loading: ReadonlySet<LeadsDataSource>;
};

/**
 * The single resolver for a leads surface: from the selection it derives which data sources the selected
 * definitions need, fetches exactly those (each query's `enabled` follows from `requires`, so it can never
 * drift from what the filters ask for), and assembles the `ctx` + `ready` set the fold consumes. Replaces
 * the FactoryDeps / `enabled`-predicate / `useMemo`-dep triple-entry bookkeeping.
 */
export const useLeadsFilterBuildContext = ({
    selection,
    applySuppressionListByDefault,
    listCompanies,
}: UseLeadsFilterBuildContextParams): LeadsFilterBuildContextResult => {
    const { neededSources, isLeadScoresSelected } = useMemo(() => {
        const selected = selectDefinitions(leadsFilterRegistry, selection);
        const sources = new Set<LeadsDataSource>();
        selected.forEach((definition) =>
            definition.requires?.forEach((source) => sources.add(source as LeadsDataSource)),
        );

        return { neededSources: sources, isLeadScoresSelected: selected.some((d) => d.key === "leadScores") };
    }, [selection]);

    const isDescriptionEnabled = neededSources.has(leadsDataSourceMap.description);
    const { data: description, isLoading: isDescriptionFetchInFlight } = useLeadsDescriptionQuery({
        enabled: isDescriptionEnabled,
    });

    const isTechnologiesEnabled = neededSources.has(leadsDataSourceMap.technologies);
    const { data: technologies, isLoading: isTechnologiesFetchInFlight } = useGroupedTechnologiesQuery({
        enabled: isTechnologiesEnabled,
    });

    // `neededSources` is derived from the static selection, so it knows nothing about opt-in or demo state and
    // alone cannot gate this — without the visibility predicate every user would download the taxonomy. Gating
    // the fetch through the *same* predicate the two cards use is what keeps "which card shows" and "which
    // data is fetched" from ever disagreeing.
    const activeDemoId = useActiveDemoId();
    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();

    const isIndustryTaxonomyEnabled =
        showsIndustryTaxonomy({ activeDemoId, isIndustryTaxonomyOptedIn }) &&
        neededSources.has(leadsDataSourceMap.industryTaxonomy);
    const { data: industryTaxonomy, isLoading: isIndustryTaxonomyFetchInFlight } = useIndustryTaxonomyQuery({
        enabled: isIndustryTaxonomyEnabled,
    });

    // `enabled &&` matters: TanStack does not abort an in-flight fetch when `enabled` flips false, so a
    // just-disabled query still reports `isLoading` — it must not count as a loading source then.
    const isDescriptionLoading = isDescriptionEnabled && isDescriptionFetchInFlight;
    const isTechnologiesLoading = isTechnologiesEnabled && isTechnologiesFetchInFlight;
    const isIndustryTaxonomyLoading = isIndustryTaxonomyEnabled && isIndustryTaxonomyFetchInFlight;

    // The scoring config feeds only the LeadScore filter (its empty-state CTA + score tiers), so it is
    // fetched only when that filter is in scope — supplementary, never a blocking `requires`.
    const { hasScoringRules, enabledLeadScoreTiers } = useLeadScoringFiltersData(isLeadScoresSelected);

    const currentStaticList = useCurrentStaticList();
    const suppressionLists: SuppressionList[] = useSelector(selectSuppressionLists);
    const areSuppressionListsReady = useSelector(selectIsListReady);
    const allOpportunityLists = useSelector(selectOpportunityLists);
    const opportunityLists: OpportunityListType[] = useMemo(
        () => allOpportunityLists.filter((c) => c.opportunityListId !== currentStaticList?.opportunityListId),
        [allOpportunityLists, currentStaticList?.opportunityListId],
    );

    const areSignalsFetching = useSelector(selectSignalsAreBeingFetched);
    const [hasSignalsLoadedOnce, setHasSignalsLoadedOnce] = useState(false);
    const signalsListViewModel = useSignalsListViewModel();

    useEffect(() => {
        if (!areSignalsFetching) {
            setHasSignalsLoadedOnce(true);
        }
    }, [areSignalsFetching]);

    const areSignalsLoading = areSignalsFetching && !hasSignalsLoadedOnce;

    const ready = useMemo(() => {
        const set = new Set<LeadsDataSource>();
        if (description) {
            set.add(leadsDataSourceMap.description);
        }
        if (technologies) {
            set.add(leadsDataSourceMap.technologies);
        }
        if (industryTaxonomy) {
            set.add(leadsDataSourceMap.industryTaxonomy);
        }
        if (areSuppressionListsReady) {
            set.add(leadsDataSourceMap.suppressionLists);
        }
        if (!areSignalsLoading) {
            set.add(leadsDataSourceMap.signals);
        }

        return set;
    }, [description, technologies, industryTaxonomy, areSuppressionListsReady, areSignalsLoading]);

    const loading = useMemo(() => {
        const set = new Set<LeadsDataSource>();
        if (isDescriptionLoading) {
            set.add(leadsDataSourceMap.description);
        }
        if (isTechnologiesLoading) {
            set.add(leadsDataSourceMap.technologies);
        }
        if (isIndustryTaxonomyLoading) {
            set.add(leadsDataSourceMap.industryTaxonomy);
        }

        return set;
    }, [isDescriptionLoading, isTechnologiesLoading, isIndustryTaxonomyLoading]);

    const ctx = useMemo(
        (): LeadsFilterBuildContext => ({
            description,
            technologies,
            industryTaxonomy,
            countryService: siCountryService,
            opportunityLists,
            suppressionLists,
            signalsListViewModel,
            hasScoringRules,
            enabledLeadScoreTiers,
            listCompanies,
            applySuppressionListByDefault,
            activeDemoId,
            isIndustryTaxonomyOptedIn,
            usage: selection.usage,
        }),
        [
            description,
            technologies,
            industryTaxonomy,
            opportunityLists,
            suppressionLists,
            signalsListViewModel,
            hasScoringRules,
            enabledLeadScoreTiers,
            listCompanies,
            applySuppressionListByDefault,
            activeDemoId,
            isIndustryTaxonomyOptedIn,
            selection.usage,
        ],
    );

    return { ctx, ready, loading };
};
